// KeyboardInput.ts — Phaser keyboard shortcut handler

import Phaser from 'phaser'
import { PerformanceMonitor } from '@engine/PerformanceMonitor.js'
import { ENV } from '../../config/env.js'

export type KeyBinding = {
  key: string // Phaser.Input.Keyboard.KeyCodes key name, e.g. 'R', 'Q', 'ESC'
  callback: () => void
  label?: string
}

export class KeyboardInput {
  private scene: Phaser.Scene
  private keys: Map<string, Phaser.Input.Keyboard.Key> = new Map()
  private bindings: KeyBinding[] = []
  private _perfShortcutHandler: ((event: KeyboardEvent) => void) | null = null

  constructor(scene: Phaser.Scene, bindings: KeyBinding[]) {
    this.scene = scene
    this.bindings = bindings
    this.register()
    this._registerDevShortcuts()
  }

  private register(): void {
    if (!this.scene.input.keyboard) return

    for (const binding of this.bindings) {
      const keyCode =
        (Phaser.Input.Keyboard.KeyCodes as Record<string, number>)[binding.key.toUpperCase()]
      if (keyCode === undefined) {
        console.warn(`[KeyboardInput] Unknown key: ${binding.key}`)
        continue
      }
      const key = this.scene.input.keyboard.addKey(keyCode)
      key.on('down', binding.callback)
      this.keys.set(binding.key.toUpperCase(), key)
    }
  }

  /**
   * Dev-only keyboard shortcuts registered via the native DOM (not Phaser's
   * key system) so they work regardless of Phaser input state.
   *
   * Shift+P — dump PerformanceMonitor report to console.table().
   */
  private _registerDevShortcuts(): void {
    if (!ENV.IS_DEV) return

    this._perfShortcutHandler = (event: KeyboardEvent) => {
      if (event.shiftKey && event.key.toUpperCase() === 'P') {
        const report = PerformanceMonitor.getReport()
        if (Object.keys(report).length === 0) {
          console.log('[Perf] No samples recorded yet. Call PerformanceMonitor.enable() first.')
        } else {
          console.log('[Perf] Performance report (last 120 samples per label):')
          console.table(report)
        }
      }
    }

    window.addEventListener('keydown', this._perfShortcutHandler)
  }

  destroy(): void {
    if (!this.scene.input.keyboard) return
    for (const [, key] of this.keys) {
      key.removeAllListeners()
      this.scene.input.keyboard.removeKey(key)
    }
    this.keys.clear()

    if (this._perfShortcutHandler) {
      window.removeEventListener('keydown', this._perfShortcutHandler)
      this._perfShortcutHandler = null
    }
  }
}
